import React from 'react';
import {
  getWeekDays,
  getWeekDaysFromStartDate,
  getItemsForDate,
  formatTime,
  getStatusColorClass,
  getItemSpanPosition,
} from './scheduleHelpers';
import classes from './WeeklyCalendar.module.css';

interface WeeklyCalendarProps {
  driverSchedules: DriverSchedule[];
  startDate?: string;
}

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({
  driverSchedules,
  startDate,
}) => {
  // Use provided start date or fall back to current week
  const weekDays = startDate
    ? getWeekDaysFromStartDate(startDate)
    : getWeekDays();

  // Generate dynamic day names from the actual dates
  const dayNames = weekDays.map(day =>
    day.toLocaleDateString('en-US', { weekday: 'long' }),
  );

  return (
    <div className={classes.calendar}>
      <table className={classes.calendarTable} role="grid">
        <thead>
          <tr>
            <th scope="col" className={classes.driverHeader}></th>
            {dayNames.map((dayName, index) => (
              <th
                key={`${dayName}-${index}`}
                scope="col"
                className={classes.dayHeader}
              >
                <div className={classes.dayName}>{dayName}</div>
                <div className={classes.dayDate}>
                  {weekDays[index].getDate()}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={classes.driversContainer}>
          {driverSchedules.map(driver => (
            <tr key={driver.driverId} className={classes.driverRow}>
              <th scope="row" className={classes.driverInfo}>
                <div className={classes.driverName}>{driver.driverName}</div>
                {driver.driverPreferredDispatchName && (
                  <div className={classes.dispatcherName}>
                    Dispatcher: {driver.driverPreferredDispatchName}
                  </div>
                )}
              </th>

              {weekDays.map((day, dayIndex) => {
                const itemsForDay = getItemsForDate(driver.scheduleItems, day);

                return (
                  <td
                    key={dayIndex}
                    className={classes.dayCell}
                    role="gridcell"
                  >
                    {itemsForDay.map(item => {
                      const spanPosition = getItemSpanPosition(item, day);
                      const spanClasses = [
                        classes.scheduleItem,
                        classes[getStatusColorClass(item)],
                      ];

                      // Add span-specific classes
                      if (spanPosition.totalDays > 1) {
                        spanClasses.push(classes.spanningItem);
                        if (spanPosition.isFirst)
                          spanClasses.push(classes.spanFirst);
                        if (spanPosition.isLast)
                          spanClasses.push(classes.spanLast);
                        if (spanPosition.isMiddle)
                          spanClasses.push(classes.spanMiddle);
                      }

                      return (
                        <div
                          key={`${item.itemId}-${dayIndex}`}
                          className={spanClasses.join(' ')}
                          title={item.notes || ''}
                        >
                          <div className={classes.itemHeader}>
                            <span className={classes.itemType}>
                              {item.itemType === 'Load'
                                ? `Load #${item.loadNumber}`
                                : item.eventType}
                              {spanPosition.totalDays > 1 && (
                                <span className={classes.spanInfo}>
                                  ({spanPosition.currentDay}/
                                  {spanPosition.totalDays})
                                </span>
                              )}
                            </span>
                            {item.status && (
                              <span className={classes.itemStatus}>
                                {item.status}
                              </span>
                            )}
                          </div>

                          {/* Show times only on first and last day for multi-day items */}
                          {!item.isAllDay &&
                            (spanPosition.isFirst ||
                              spanPosition.totalDays === 1) && (
                              <div className={classes.itemTime}>
                                {formatTime(item.startDateTime)}
                                {spanPosition.totalDays === 1 &&
                                  item.endDateTime &&
                                  ` - ${formatTime(item.endDateTime)}`}
                              </div>
                            )}

                          {!item.isAllDay &&
                            spanPosition.isLast &&
                            spanPosition.totalDays > 1 &&
                            item.endDateTime && (
                              <div className={classes.itemTime}>
                                Ends: {formatTime(item.endDateTime)}
                              </div>
                            )}

                          {item.isAllDay && (
                            <div className={classes.allDayLabel}>All Day</div>
                          )}

                          {/* Show location details only on first day for multi-day loads */}
                          {item.itemType === 'Load' && spanPosition.isFirst && (
                            <>
                              {item.origin && (
                                <div className={classes.location}>
                                  <strong>From:</strong> {item.origin}
                                </div>
                              )}
                              {item.miles && (
                                <div className={classes.miles}>
                                  {Math.round(item.miles)} miles
                                </div>
                              )}
                            </>
                          )}

                          {/* Show destination on last day for multi-day loads */}
                          {item.itemType === 'Load' &&
                            spanPosition.isLast &&
                            item.destination && (
                              <div className={classes.location}>
                                <strong>To:</strong> {item.destination}
                              </div>
                            )}

                          {/* Show notes only on first day */}
                          {item.notes && spanPosition.isFirst && (
                            <div className={classes.notes}>{item.notes}</div>
                          )}
                        </div>
                      );
                    })}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WeeklyCalendar;
